import React from 'react';
import { useTranslation } from 'react-i18next';

import { IColumn, PINNED, UNITS } from '@private/data-grid';

import { IReport, IReportsPath } from 'api/reports/types';
import {
  renderCenterHeaderCell,
  renderHeaderCell,
  renderRowCell,
} from 'utils/grid/sharedRenderers';
import { IUseGridDataResult } from 'utils/types';

import { ActionsCell, DateCell, SizeCell, StatusCell } from './cells';

const prefix = 'transactions.report';

interface IReportsGridDataProps extends IReportsPath {
  data?: IReport[];
}

export const useReportsGridData = ({
  data,
  path,
}: IReportsGridDataProps): IUseGridDataResult<IReport> => {
  const { t } = useTranslation();

  const reportColumns = React.useMemo(() => {
    return [
      {
        dataKey: 'id',
        minWidth: 90,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.columns.id`),
        width: { unit: UNITS.PERCENT, value: 5 },
      },
      {
        dataKey: 'status',
        minWidth: 120,
        renderHeaderCell,
        renderRowCell: StatusCell,
        title: t(`${prefix}.columns.status`),
        width: { unit: UNITS.PERCENT, value: 10 },
      },
      {
        dataKey: 'fileName',
        minWidth: 170,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.columns.fileName`),
        width: { unit: UNITS.PERCENT, value: 25 },
      },
      {
        dataKey: 'created',
        renderHeaderCell,
        renderRowCell: (params) => <DateCell {...params} date={params.item.created} />,
        title: t(`${prefix}.columns.created`),
        width: { unit: UNITS.PERCENT, value: 20 },
      },
      {
        dataKey: 'completed',
        renderHeaderCell,
        renderRowCell: (params) => <DateCell {...params} date={params.item.completed} />,
        title: t(`${prefix}.columns.completed`),
        width: { unit: UNITS.PERCENT, value: 20 },
      },
      {
        dataKey: 'size',
        minWidth: 90,
        renderHeaderCell,
        renderRowCell: SizeCell,
        title: t(`${prefix}.columns.size`),
        width: { unit: UNITS.PERCENT, value: 5 },
      },
      {
        dataKey: 'deleteColumn' as never,
        minWidth: 140,
        pinned: PINNED.FIXED_RIGHT,
        renderHeaderCell: renderCenterHeaderCell,
        renderRowCell: (params) => <ActionsCell {...params} path={path} />,
        title: '',
        width: { unit: UNITS.PERCENT, value: 10 },
      },
    ] as IColumn<IReport>[];
  }, [path, t]);

  return !data ? [] : reportColumns;
};
